import type { Role } from '@kanbu/schema/enums';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

/**
 * Custom types for analytics events
 */
export interface AnalyticsEvents {
  userRegistered: {
    email: string;
    organization: string;
  };
}

/**
 * Custom hook for PostHog analytics with SaaS-specific tracking
 */
export function useAnalytics() {
  const posthog = usePostHog();

  /**
   * Identify logged in user for analytics
   */
  const identify = useCallback(
    (
      userId: string,
      properties?: {
        email: string;
        name: string;
        organization: string;
        organizationId: string;
        firstLogin: boolean;
        lastLogin: string;
        role: Role;
      },
    ) => {
      if (!posthog) {
        return;
      }

      posthog.identify(userId, {
        ...properties,
      });
    },
    [posthog],
  );

  /**
   * Track events for analytics
   */
  const trackEvent = useCallback(
    <T extends keyof AnalyticsEvents>(
      event: T,
      properties?: AnalyticsEvents[T] & Record<string, any>,
    ) => {
      if (!posthog) {
        return;
      }

      posthog.capture(event, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog],
  );

  /**
   * Track errors for analytics
   */
  const trackError = useCallback(
    (error: Error, context?: Record<string, any>) => {
      if (!posthog) {
        return;
      }

      // Use PostHog's captureException for better error tracking
      posthog.captureException(error, {
        $exception_fingerprint: `${error.name}-${context?.feature || 'unknown'}`,
        context,
      });
    },
    [posthog],
  );

  return {
    identify,
    trackEvent,
    trackError,
    posthog,
  };
}
